import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import inboxImage from "../../assets/lead-inbox.webp";

function ContactSection() {
  const [role, setRole] = useState("DSA");
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const roles = ["DSA", "Partner lender", "Borrower"];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name
    if (!formData.name.trim()) {
      newErrors.name = "Please enter your name";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    // Phone (10-digit Indian mobile)
    const phoneClean = formData.phone.trim().replace(/\D/g, "");
    if (!formData.phone.trim()) {
      newErrors.phone = "Please enter your mobile number";
    } else if (!/^[6-9]\d{9}$/.test(phoneClean)) {
      newErrors.phone = "Enter a valid 10-digit mobile number";
    }

    // Email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Please enter your email";
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = "Enter a valid email address";
    }

    // Message
    if (!formData.message.trim()) {
      newErrors.message = "Please write a message";
    } else if (formData.message.trim().length < 5) {
      newErrors.message = "Message must be at least 5 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill in all required fields properly.", {
        position: "bottom-right",
        theme: "dark",
        autoClose: 3500,
      });
      return;
    }

    setIsSubmitting(true);

    setTimeout(() => {
      setIsSubmitting(false);
      toast.success(
        `Thank you ${formData.name.trim()}! We have received your message as a ${role}. We will reply within one working day.`,
        {
          position: "bottom-right",
          theme: "dark",
          autoClose: 5000,
        }
      );

      // Reset form
      setFormData({
        name: "",
        phone: "",
        email: "",
        message: "",
      });
      setErrors({});
    }, 600);
  };

  return (
    <section id="contact-form" className="overflow-hidden bg-[#f8faf9] section-spacing">
      <div className="section-container">
        <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-12 lg:gap-12 xl:gap-16">
          {/* ================= LEFT: FORM CARD ================= */}
          <div className="lg:col-span-7">
            <div className="rounded-[24px] border border-[#e2e9e5] bg-white p-6 shadow-[0_20px_50px_rgba(0,0,0,0.03)] sm:rounded-[28px] sm:p-8 md:p-10">
              <form onSubmit={handleSubmit} noValidate>
                {/* I AM A (Role Selector) */}
                <div>
                  <p className="font-jakarta text-[10px] font-semibold uppercase tracking-[0.16em] text-[#7d8b85] sm:text-[11px]">
                    I AM A
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2.5 sm:gap-3">
                    {roles.map((r) => {
                      const isSelected = role === r;
                      return (
                        <button
                          key={r}
                          type="button"
                          onClick={() => setRole(r)}
                          className={`rounded-full px-5 py-2 font-jakarta text-[13px] font-medium transition-all sm:text-[14px] cursor-pointer ${
                            isSelected
                              ? "bg-[var(--mint-green)] font-semibold text-[#071b17] shadow-sm"
                              : "border border-[#e2e9e5] bg-white text-[#556369] hover:border-[#cbd7d0]"
                          }`}
                        >
                          {r}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* NAME & PHONE */}
                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6">
                  {/* Name */}
                  <div>
                    <label
                      htmlFor="name"
                      className="block font-jakarta text-[10px] font-semibold uppercase tracking-[0.16em] text-[#7d8b85] sm:text-[11px]"
                    >
                      NAME
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className={`mt-2 w-full rounded-xl border bg-[#fbfdfc] px-4 py-3.5 font-jakarta text-[14px] text-[#0b1720] placeholder:text-[#99a6a0] sm:text-[15px] focus:outline-none transition-colors ${
                        errors.name
                          ? "border-red-500 focus:border-red-500"
                          : "border-[#e2e9e5] focus:border-[var(--mint-green)]"
                      }`}
                    />
                    {errors.name && (
                      <p className="mt-1.5 font-jakarta text-[12px] text-red-500">
                        {errors.name}
                      </p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label
                      htmlFor="phone"
                      className="block font-jakarta text-[10px] font-semibold uppercase tracking-[0.16em] text-[#7d8b85] sm:text-[11px]"
                    >
                      PHONE
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="10–digit mobile"
                      maxLength={10}
                      className={`mt-2 w-full rounded-xl border bg-[#fbfdfc] px-4 py-3.5 font-jakarta text-[14px] text-[#0b1720] placeholder:text-[#99a6a0] sm:text-[15px] focus:outline-none transition-colors ${
                        errors.phone
                          ? "border-red-500 focus:border-red-500"
                          : "border-[#e2e9e5] focus:border-[var(--mint-green)]"
                      }`}
                    />
                    {errors.phone && (
                      <p className="mt-1.5 font-jakarta text-[12px] text-red-500">
                        {errors.phone}
                      </p>
                    )}
                  </div>
                </div>

                {/* EMAIL */}
                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6">
                  <div>
                    <label
                      htmlFor="email"
                      className="block font-jakarta text-[10px] font-semibold uppercase tracking-[0.16em] text-[#7d8b85] sm:text-[11px]"
                    >
                      EMAIL
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="name@example.com"
                      className={`mt-2 w-full rounded-xl border bg-[#fbfdfc] px-4 py-3.5 font-jakarta text-[14px] text-[#0b1720] placeholder:text-[#99a6a0] sm:text-[15px] focus:outline-none transition-colors ${
                        errors.email
                          ? "border-red-500 focus:border-red-500"
                          : "border-[#e2e9e5] focus:border-[var(--mint-green)]"
                      }`}
                    />
                    {errors.email && (
                      <p className="mt-1.5 font-jakarta text-[12px] text-red-500">
                        {errors.email}
                      </p>
                    )}
                  </div>
                </div>

                {/* MESSAGE */}
                <div className="mt-6">
                  <label
                    htmlFor="message"
                    className="block font-jakarta text-[10px] font-semibold uppercase tracking-[0.16em] text-[#7d8b85] sm:text-[11px]"
                  >
                    MESSAGE
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="How can we help?"
                    className={`mt-2 w-full rounded-xl border bg-[#fbfdfc] p-4 font-jakarta text-[14px] text-[#0b1720] placeholder:text-[#99a6a0] resize-none sm:text-[15px] focus:outline-none transition-colors ${
                      errors.message
                        ? "border-red-500 focus:border-red-500"
                        : "border-[#e2e9e5] focus:border-[var(--mint-green)]"
                    }`}
                  />
                  {errors.message && (
                    <p className="mt-1.5 font-jakarta text-[12px] text-red-500">
                      {errors.message}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <div className="mt-6 sm:mt-8">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex h-[50px] items-center justify-center rounded-full bg-[var(--mint-green)] px-8 font-jakarta text-[15px] font-semibold text-[#071b17] shadow-lg shadow-[var(--mint-green)]/20 transition-all duration-200 hover:brightness-105 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 cursor-pointer sm:h-[52px] sm:px-9"
                  >
                    {isSubmitting ? "Sending..." : "Send message"}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* ================= RIGHT: INFO & SKIP THE QUEUE ================= */}
          <div className="lg:col-span-5">
            {/* Top Contact Info Card */}
            <div className="rounded-[24px] border border-[#e2e9e5] bg-white p-7 shadow-[0_15px_40px_rgba(0,0,0,0.03)] sm:rounded-[28px] sm:p-8 md:p-9">
              {/* 3D Inbox Icon */}
              <div className="w-[50px] sm:w-[56px]">
                <img
                  src={inboxImage}
                  alt="Inbox mail"
                  className="h-auto w-full object-contain"
                  loading="lazy"
                  decoding="async"
                />
              </div>

              {/* Email & Phone */}
              <div className="mt-5 space-y-1">
                <a
                  href="mailto:supportfinserv@gmail.com"
                  className="block font-jakarta text-[15px] font-medium text-[var(--mint-green)] sm:text-[16px] hover:underline"
                >
                  supportfinserv@gmail.com
                </a>
                <p className="font-jakarta text-[15px] font-medium text-[#9fa9a5] sm:text-[16px]">
                  Mon–Sat, 10:00–19:00 IST
                </p>
              </div>

              {/* Mon-Sat details */}
              <p className="mt-4 font-jakarta text-[13px] leading-[1.6] text-[#7d8b85] sm:text-[14px]">
                Parthv Capverse Advisory Private Limited<br />
                C-501, 5th Floor, Vipul Plaza, Sector 81, Amarnagar (Faridabad) - 121003, Haryana, India
              </p>
            </div>

            {/* Bottom Skip The Queue */}
            <div className="mt-8 sm:mt-10 lg:mt-12">
              <p className="font-jakarta text-[10px] font-semibold uppercase tracking-[0.24em] text-[var(--mint-green)] sm:text-[11px]">
                SKIP THE QUEUE
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-3.5 sm:gap-4">
                <Link
                  to="/for-dsas"
                  className="inline-flex h-[48px] items-center justify-center rounded-full bg-[var(--mint-green)] px-6 font-jakarta text-[14px] font-semibold text-[#071b17] shadow-sm transition-all duration-200 hover:brightness-105 hover:-translate-y-0.5 sm:h-[50px] sm:px-7 sm:text-[15px]"
                >
                  Join as a DSA
                </Link>

                <Link
                  to="/for-lenders"
                  className="inline-flex h-[48px] items-center justify-center rounded-full border border-[#e2e9e5] bg-white px-6 font-jakarta text-[14px] font-semibold text-[#556369] shadow-sm transition-all duration-200 hover:border-[#cbd7d0] hover:-translate-y-0.5 sm:h-[50px] sm:px-7 sm:text-[15px]"
                >
                  Partner as a lender
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast notifications */}
      <ToastContainer />
    </section>
  );
}

export default ContactSection;
